import { useState, useMemo, useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Loader2, Plus, Building2, MapPin, Phone, Mail, Globe, ExternalLink,
  Pencil, Trash2, Copy, Store, CalendarDays, Info, Crown,
  BarChart3, Clock, TrendingUp, CheckCircle2, XCircle, CalendarX2,
  DollarSign, ChevronLeft, Users, Scissors, ImagePlus, X,
  Bot, Wifi, WifiOff, QrCode, RefreshCw, Pause, Play, Link, Eye, EyeOff, Save,
} from "lucide-react";

type Branch = {
  id: number;
  parentSalonId: number;
  name: string;
  slug: string;
  description: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  logoUrl: string | null;
  status: "active" | "inactive";
  createdAt: Date;
};

const emptyForm = {
  name: "",
  slug: "",
  description: "",
  address: "",
  phone: "",
  email: "",
  logoUrl: "",
  status: "active" as "active" | "inactive",
};

const DAY_NAMES = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function slugify(text: string) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

// ── Painel de métricas de uma filial ────────────────────────────────────────
function BranchMetricsPanel({ branch }: { branch: Branch }) {
  const [period, setPeriod] = useState<"7" | "30" | "90">("30");
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ description: "", category: "other", amount: "", occurredAt: new Date().toISOString().slice(0, 10) });
  const utils = trpc.useUtils();

  const [startDate, endDate] = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - parseInt(period));
    return [start, end];
  }, [period]);

  // Stable query input — inline object causes infinite re-fetches with Date values
  const metricsInput = useMemo(
    () => ({ branchId: branch.id, startDate, endDate }),
    [branch.id, startDate, endDate]
  );
  const { data, isLoading } = (trpc as any).branches.getMetrics.useQuery(
    metricsInput,
    { enabled: !!branch.id }
  );
  const expensesInput = useMemo(() => ({ branchId: branch.id, startDate, endDate }), [branch.id, startDate, endDate]);
  const { data: expenses = [] } = (trpc as any).branches.listExpenses.useQuery(expensesInput, { enabled: !!branch.id });
  const createExpenseMutation = (trpc as any).branches.createExpense.useMutation({
    onSuccess: () => { toast.success("Despesa registrada na rentabilidade da filial."); setShowExpenseDialog(false); setExpenseForm({ description: "", category: "other", amount: "", occurredAt: new Date().toISOString().slice(0, 10) }); utils.branches.getMetrics.invalidate(); utils.branches.listExpenses.invalidate(); },
    onError: (error: Error) => toast.error(error.message),
  });
  const deleteExpenseMutation = (trpc as any).branches.deleteExpense.useMutation({
    onSuccess: () => { utils.branches.getMetrics.invalidate(); utils.branches.listExpenses.invalidate(); toast.success("Despesa removida."); },
    onError: (error: Error) => toast.error(error.message),
  });

  const statusMap: Record<string, { label: string; color: string }> = {
    pending:   { label: "Pendente",   color: "text-yellow-500" },
    confirmed: { label: "Confirmado", color: "text-blue-500" },
    completed: { label: "Concluído",  color: "text-green-500" },
    cancelled: { label: "Cancelado",  color: "text-red-500" },
    no_show:   { label: "Não compareceu", color: "text-orange-500" },
  };

  const totalAppts = (data?.statusCounts ?? []).reduce((acc: number, s: any) => acc + Number(s.count), 0);

  return (
    <div className="space-y-5">
      {/* Seletor de período */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Período:</span>
        {(["7", "30", "90"] as const).map((p) => (
          <Button
            key={p}
            variant={period === p ? "default" : "outline"}
            size="sm"
            onClick={() => setPeriod(p)}
            className="text-xs"
          >
            {p === "7" ? "7 dias" : p === "30" ? "30 dias" : "90 dias"}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Cards de resumo */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Receita</p>
                  <p className="text-xl font-bold text-green-500">
                    R$ {Number(data?.revenue?.total ?? 0).toFixed(2).replace(".", ",")}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Agendamentos</p>
                  <p className="text-xl font-bold">{totalAppts}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="col-span-2 lg:col-span-1">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Margem após comissão</p>
                  <p className="text-xl font-bold text-violet-700">R$ {Number(data?.profitability?.operationalMargin ?? 0).toFixed(2).replace(".", ",")}</p>
                  <p className="text-[11px] text-muted-foreground">{Number(data?.profitability?.operationalMarginPercent ?? 0).toFixed(1).replace(".", ",")}% · sem despesas fixas</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Ocupação da agenda</p><p className="mt-1 text-xl font-bold text-sky-700">{data?.operational?.occupancyPercent === null || data?.operational?.occupancyPercent === undefined ? "—" : `${Number(data.operational.occupancyPercent).toFixed(1).replace(".", ",")}%`}</p><p className="mt-1 text-[11px] text-muted-foreground">{data?.operational?.capacityMinutes ? `${Math.round(Number(data.operational.bookedMinutes ?? 0) / 60)}h ocupadas de ${Math.round(Number(data.operational.capacityMinutes) / 60)}h disponíveis` : "Configure horários e equipe da filial"}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Recorrência</p><p className="mt-1 text-xl font-bold text-indigo-700">{Number(data?.operational?.repeatClientRate ?? 0).toFixed(1).replace(".", ",")}%</p><p className="mt-1 text-[11px] text-muted-foreground">Clientes atendidas que retornaram no período</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Conversão</p><p className="mt-1 text-xl font-bold text-emerald-700">{Number(data?.operational?.conversionRate ?? 0).toFixed(1).replace(".", ",")}%</p><p className="mt-1 text-[11px] text-muted-foreground">Atendimentos concluídos entre os resolvidos</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Faltas</p><p className="mt-1 text-xl font-bold text-rose-700">{Number(data?.operational?.noShowRate ?? 0).toFixed(1).replace(".", ",")}%</p><p className="mt-1 text-[11px] text-muted-foreground">Equipe ativa: {Number(data?.operational?.activeProfessionals ?? 0)}</p></CardContent></Card>
          </div>

          <Card className="border-violet-200 bg-violet-50/30">
            <CardHeader className="flex-row items-center justify-between gap-3 pb-3">
              <div><CardTitle className="text-sm font-semibold">Composição da margem</CardTitle><CardDescription>Receita concluída menos custos vinculados à filial no período.</CardDescription></div>
              <Button size="sm" variant="outline" onClick={() => setShowExpenseDialog(true)}><Plus className="mr-1 h-3.5 w-3.5" />Lançar despesa</Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4"><div><p className="text-xs text-muted-foreground">Comissões</p><p className="font-semibold text-rose-700">- R$ {Number(data?.profitability?.commissions ?? 0).toFixed(2).replace(".", ",")}</p></div><div><p className="text-xs text-muted-foreground">Despesas da filial</p><p className="font-semibold text-rose-700">- R$ {Number(data?.profitability?.branchExpenses ?? 0).toFixed(2).replace(".", ",")}</p></div><div><p className="text-xs text-muted-foreground">Consumo de produtos</p><p className="font-semibold text-muted-foreground">R$ {Number(data?.profitability?.productConsumption ?? 0).toFixed(2).replace(".", ",")}*</p></div><div><p className="text-xs text-muted-foreground">Margem operacional</p><p className="font-bold text-violet-700">R$ {Number(data?.profitability?.operationalMargin ?? 0).toFixed(2).replace(".", ",")}</p></div></div>
              <p className="text-[11px] text-muted-foreground">* O consumo considera saídas de estoque vinculadas a esta filial. Movimentos antigos sem filial permanecem fora do rateio para não atribuir custos incorretos.</p>
              {expenses.length > 0 && <div className="space-y-2 border-t pt-3"><p className="text-xs font-medium text-muted-foreground">Despesas lançadas no período</p>{expenses.slice(0, 5).map((expense: any) => <div key={expense.id} className="flex items-center justify-between gap-3 rounded-lg border bg-background p-2"><div className="min-w-0"><p className="truncate text-sm font-medium">{expense.description}</p><p className="text-xs text-muted-foreground">{new Date(expense.occurredAt).toLocaleDateString("pt-BR")} · {expense.category}</p></div><div className="flex items-center gap-2"><span className="font-semibold text-rose-700">R$ {Number(expense.amount).toFixed(2).replace(".", ",")}</span><Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => deleteExpenseMutation.mutate({ branchId: branch.id, id: expense.id })} disabled={deleteExpenseMutation.isPending}><Trash2 className="h-3.5 w-3.5" /></Button></div></div>)}</div>}
            </CardContent>
          </Card>

          {/* Status breakdown */}
          {(data?.statusCounts ?? []).length > 0 ? (
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold">Por status</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2">
                {(data.statusCounts as any[]).map((sc: any) => {
                  const meta = statusMap[sc.status] ?? { label: sc.status, color: "text-foreground" };
                  const pct = totalAppts > 0 ? Math.round((Number(sc.count) / totalAppts) * 100) : 0;
                  return (
                    <div key={sc.status} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className={`font-medium ${meta.color}`}>{meta.label}</span>
                        <span className="text-muted-foreground">{sc.count} ({pct}%)</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary/60 transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
              Nenhum agendamento neste período para esta filial.
            </div>
          )}
        </>
      )}
      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle>Lançar despesa da filial</DialogTitle></DialogHeader><div className="space-y-4 py-2"><div className="space-y-2"><Label>Descrição</Label><Input value={expenseForm.description} onChange={(event) => setExpenseForm((current) => ({ ...current, description: event.target.value }))} placeholder="Ex.: aluguel da unidade" /></div><div className="grid grid-cols-2 gap-3"><div className="space-y-2"><Label>Categoria</Label><Select value={expenseForm.category} onValueChange={(value) => setExpenseForm((current) => ({ ...current, category: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="rent">Aluguel</SelectItem><SelectItem value="utilities">Utilidades</SelectItem><SelectItem value="marketing">Marketing</SelectItem><SelectItem value="supplies">Insumos</SelectItem><SelectItem value="other">Outra</SelectItem></SelectContent></Select></div><div className="space-y-2"><Label>Valor</Label><Input inputMode="decimal" value={expenseForm.amount} onChange={(event) => setExpenseForm((current) => ({ ...current, amount: event.target.value.replace(",", ".") }))} placeholder="0,00" /></div></div><div className="space-y-2"><Label>Data</Label><Input type="date" value={expenseForm.occurredAt} onChange={(event) => setExpenseForm((current) => ({ ...current, occurredAt: event.target.value }))} /></div></div><DialogFooter><Button variant="outline" onClick={() => setShowExpenseDialog(false)}>Cancelar</Button><Button onClick={() => createExpenseMutation.mutate({ branchId: branch.id, description: expenseForm.description.trim(), category: expenseForm.category, amount: Number(expenseForm.amount), occurredAt: new Date(`${expenseForm.occurredAt}T12:00:00`) })} disabled={!expenseForm.description.trim() || Number(expenseForm.amount) <= 0 || createExpenseMutation.isPending}>{createExpenseMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Salvar despesa</Button></DialogFooter></DialogContent>
      </Dialog>
    </div>
  );
}

function BranchComparisonCard({ branch, startDate, endDate }: { branch: Branch; startDate: Date; endDate: Date }) {
  const { data, isLoading } = (trpc as any).branches.getMetrics.useQuery({ branchId: branch.id, startDate, endDate }, { enabled: !!branch.id });
  const totalAppointments = (data?.statusCounts ?? []).reduce((sum: number, item: any) => sum + Number(item.count ?? 0), 0);
  return <Card className="min-w-[220px]"><CardContent className="p-4"><div className="flex items-start justify-between gap-2"><div className="min-w-0"><p className="truncate text-sm font-semibold">{branch.name}</p><p className="mt-0.5 text-xs text-muted-foreground">Período selecionado</p></div><Badge variant={branch.status === "active" ? "secondary" : "outline"}>{branch.status === "active" ? "Ativa" : "Inativa"}</Badge></div>{isLoading ? <div className="mt-4 h-16 animate-pulse rounded bg-muted" /> : <div className="mt-4 grid grid-cols-2 gap-3"><div><p className="text-[11px] text-muted-foreground">Receita</p><p className="mt-1 text-base font-bold text-emerald-600">R$ {Number(data?.revenue?.total ?? 0).toFixed(0)}</p></div><div><p className="text-[11px] text-muted-foreground">Agenda</p><p className="mt-1 text-base font-bold">{totalAppointments}</p></div><div className="col-span-2 border-t pt-3"><p className="text-[11px] text-muted-foreground">Margem após comissão</p><p className="mt-1 text-base font-bold text-violet-700">R$ {Number(data?.profitability?.operationalMargin ?? 0).toFixed(0)} · {Number(data?.profitability?.operationalMarginPercent ?? 0).toFixed(1).replace(".", ",")}%</p><p className="mt-0.5 text-[10px] text-muted-foreground">Não inclui despesas fixas e produtos.</p></div></div>}</CardContent></Card>;
}

function BranchComparison({ branches }: { branches: Branch[] }) {
  const [period, setPeriod] = useState<"7" | "30" | "90">("30");
  const [rankingMetric, setRankingMetric] = useState<"revenue" | "margin" | "occupancy">("revenue");
  const [startDate, endDate] = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - Number(period));
    return [start, end];
  }, [period]);
  const comparisonInput = useMemo(() => ({ startDate, endDate }), [startDate, endDate]);
  const { data: comparison = [], isLoading } = (trpc as any).branches.comparison.useQuery(comparisonInput, { enabled: branches.length >= 2 });
  const rankedComparison = useMemo(() => {
    const valueFor = (item: any) => {
      if (rankingMetric === "margin") return Number(item.profitability?.operationalMargin ?? 0);
      if (rankingMetric === "occupancy") return Number(item.operational?.occupancyPercent ?? -1);
      return Number(item.revenue ?? 0);
    };
    return [...comparison].sort((left: any, right: any) => valueFor(right) - valueFor(left));
  }, [comparison, rankingMetric]);
  const rankingLabels = { revenue: "receita", margin: "margem operacional", occupancy: "ocupação" } as const;
  if (branches.length < 2) return null;
  return <Card className="border-primary/20"><CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="h-4 w-4 text-primary" />Comparação executiva entre unidades</CardTitle><CardDescription>Receita, ticket médio, conversão, ocupação, recorrência e margem operacional no mesmo período.</CardDescription></CardHeader><CardContent className="space-y-4"><div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"><div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">Período:</span>{(["7", "30", "90"] as const).map((option) => <Button key={option} variant={period === option ? "default" : "outline"} size="sm" onClick={() => setPeriod(option)} className="text-xs">{option} dias</Button>)}</div><div className="flex flex-wrap items-center gap-1.5"><span className="text-xs text-muted-foreground">Ranking:</span>{(["revenue", "margin", "occupancy"] as const).map((option) => <Button key={option} variant={rankingMetric === option ? "default" : "outline"} size="sm" onClick={() => setRankingMetric(option)} className="h-7 text-xs">{option === "revenue" ? "Receita" : option === "margin" ? "Margem" : "Ocupação"}</Button>)}</div></div><p className="text-xs text-muted-foreground">Unidades ordenadas por <span className="font-medium text-foreground">{rankingLabels[rankingMetric]}</span>.</p>{isLoading ? <div className="flex gap-3 overflow-x-auto pb-1">{branches.map((branch) => <div key={branch.id} className="h-36 min-w-[220px] animate-pulse rounded-xl bg-muted" />)}</div> : <div className="flex gap-3 overflow-x-auto pb-1">{rankedComparison.map((item: any, index: number) => <Card key={item.branch.id} className="min-w-[260px]"><CardContent className="p-4"><div className="flex items-start justify-between gap-2"><div className="min-w-0"><p className="truncate text-sm font-semibold">{item.branch.name}</p><p className="mt-0.5 text-xs text-muted-foreground">{item.completed} atendimento(s) concluído(s)</p></div><Badge variant={index === 0 ? "default" : "secondary"}>#{index + 1}</Badge></div><div className="mt-4 grid grid-cols-2 gap-3"><div><p className="text-[11px] text-muted-foreground">Receita</p><p className="mt-1 font-bold text-emerald-600">R$ {Number(item.revenue).toFixed(0)}</p></div><div><p className="text-[11px] text-muted-foreground">Ticket médio</p><p className="mt-1 font-bold">R$ {Number(item.averageTicket).toFixed(0)}</p></div><div><p className="text-[11px] text-muted-foreground">Ocupação</p><p className="mt-1 font-bold">{item.operational?.occupancyPercent === null || item.operational?.occupancyPercent === undefined ? "—" : `${Number(item.operational.occupancyPercent).toFixed(1).replace(".", ",")}%`}</p></div><div><p className="text-[11px] text-muted-foreground">Recorrência</p><p className="mt-1 font-bold">{Number(item.operational?.repeatClientRate ?? 0).toFixed(1).replace(".", ",")}%</p></div><div><p className="text-[11px] text-muted-foreground">Conversão</p><p className="mt-1 font-bold">{Number(item.operational?.conversionRate ?? item.conversionRate).toFixed(1).replace(".", ",")}%</p></div><div><p className="text-[11px] text-muted-foreground">Margem</p><p className="mt-1 font-bold text-violet-700">R$ {Number(item.profitability?.operationalMargin ?? 0).toFixed(0)}</p></div></div></CardContent></Card>)}</div>}</CardContent></Card>;
}

// ── Painel de horário de funcionamento de uma filial ─────────────────────────
function BranchWorkingHoursPanel({ branch }: { branch: Branch }) {
  const utils = trpc.useUtils();

  const { data: hours, isLoading } = (trpc as any).branches.getWorkingHours.useQuery(
    { branchId: branch.id },
    { enabled: !!branch.id }
  );

  // Estado local do formulário: array de 7 dias
  const defaultSchedule = DAY_NAMES.map((_, i) => ({
    dayOfWeek: i,
    isOpen: i >= 1 && i <= 6, // Seg–Sáb abertos por padrão
    openTime: "08:00",
    closeTime: "18:00",
  }));

  const [schedule, setSchedule] = useState<Array<{
    dayOfWeek: number;
    isOpen: boolean;
    openTime: string;
    closeTime: string;
  }>>(defaultSchedule);

  // Preencher com dados do banco quando carregarem — dentro de useEffect para evitar React error #185
  useEffect(() => {
    if (isLoading || hours === undefined) return;
    if ((hours as any[]).length > 0) {
      setSchedule(
        DAY_NAMES.map((_, i) => {
          const h = (hours as any[]).find((x: any) => x.dayOfWeek === i);
          return {
            dayOfWeek: i,
            isOpen: h ? h.isOpen : false,
            openTime: h?.openTime?.slice(0, 5) ?? "08:00",
            closeTime: h?.closeTime?.slice(0, 5) ?? "18:00",
          };
        })
      );
    }
  }, [hours, isLoading]);

  const saveMutation = (trpc as any).branches.setWorkingHours.useMutation({
    onSuccess: () => {
      toast.success("Horários salvos!");
      (utils as any).branches.getWorkingHours.invalidate({ branchId: branch.id });
    },
    onError: (e: any) => toast.error(e.message),
  });

  function updateDay(idx: number, field: string, value: any) {
    setSchedule((prev) => prev.map((d) => d.dayOfWeek === idx ? { ...d, [field]: value } : d));
  }

  function handleSave() {
    saveMutation.mutate({ branchId: branch.id, schedule });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Configure o horário de funcionamento desta filial. Quando não configurado, o cliente verá o horário da sede.
      </p>
      <div className="space-y-2">
        {schedule.map((day) => (
          <div key={day.dayOfWeek} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0">
            <span className="w-8 text-sm font-medium text-foreground">{DAY_NAMES[day.dayOfWeek]}</span>
            <Switch
              checked={day.isOpen}
              onCheckedChange={(v) => updateDay(day.dayOfWeek, "isOpen", v)}
            />
            {day.isOpen ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  type="time"
                  value={day.openTime}
                  onChange={(e) => updateDay(day.dayOfWeek, "openTime", e.target.value)}
                  className="w-28 text-sm"
                />
                <span className="text-muted-foreground text-xs">até</span>
                <Input
                  type="time"
                  value={day.closeTime}
                  onChange={(e) => updateDay(day.dayOfWeek, "closeTime", e.target.value)}
                  className="w-28 text-sm"
                />
              </div>
            ) : (
              <span className="text-sm text-muted-foreground/50 flex-1">Fechado</span>
            )}
          </div>
        ))}
      </div>
      <Button onClick={handleSave} disabled={saveMutation.isPending} className="w-full">
        {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Salvar horários
      </Button>
    </div>
  );
}

// ── Painel de profissionais de uma filial ──────────────────────────────────
function BranchProfessionalsPanel({ branch }: { branch: Branch }) {
  const utils = trpc.useUtils();

  const { data: linkedIds = [], isLoading: loadingLinks } = (trpc as any).branches.getProfessionals.useQuery(
    { branchId: branch.id },
    { enabled: !!branch.id }
  );
  const { data: allProfessionals = [], isLoading: loadingProfs } = (trpc as any).professionals.listAdmin.useQuery();

  const linkMutation = (trpc as any).branches.linkProfessional.useMutation({
    onSuccess: () => {
      toast.success("Profissional vinculado!");
      (utils as any).branches.getProfessionals.invalidate({ branchId: branch.id });
    },
    onError: (e: any) => toast.error(e.message),
  });
  const unlinkMutation = (trpc as any).branches.unlinkProfessional.useMutation({
    onSuccess: () => {
      toast.success("Vínculo removido!");
      (utils as any).branches.getProfessionals.invalidate({ branchId: branch.id });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const isLoading = loadingLinks || loadingProfs;

  function toggle(profId: number, isLinked: boolean) {
    if (isLinked) {
      unlinkMutation.mutate({ branchId: branch.id, professionalId: profId });
    } else {
      linkMutation.mutate({ branchId: branch.id, professionalId: profId });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Selecione os profissionais que atendem nesta filial. Apenas eles aparecerão disponíveis para agendamento nesta unidade.
      </p>
      {(allProfessionals as any[]).length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
          Nenhum profissional cadastrado ainda.
        </div>
      ) : (
        <div className="space-y-2">
          {(allProfessionals as any[]).map((prof: any) => {
            const isLinked = (linkedIds as number[]).includes(prof.id);
            return (
              <div
                key={prof.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  isLinked ? "border-primary/40 bg-primary/5" : "border-border hover:bg-muted/40"
                }`}
                onClick={() => toggle(prof.id, isLinked)}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  isLinked ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                }`}>
                  {prof.name?.charAt(0).toUpperCase() ?? "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{prof.name}</p>
                  {prof.specialty && <p className="text-xs text-muted-foreground truncate">{prof.specialty}</p>}
                </div>
                <Badge variant={isLinked ? "default" : "outline"} className="text-xs shrink-0">
                  {isLinked ? "Vinculado" : "Não vinculado"}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Painel de serviços de uma filial ─────────────────────────────────────────
function BranchServicesPanel({ branch }: { branch: Branch }) {
  const utils = trpc.useUtils();

  const { data: linkedIds = [], isLoading: loadingLinks } = (trpc as any).branches.getServices.useQuery(
    { branchId: branch.id },
    { enabled: !!branch.id }
  );
  const { data: allServices = [], isLoading: loadingServices } = (trpc as any).services.listAdmin.useQuery();

  const linkMutation = (trpc as any).branches.linkService.useMutation({
    onSuccess: () => {
      toast.success("Serviço vinculado!");
      (utils as any).branches.getServices.invalidate({ branchId: branch.id });
    },
    onError: (e: any) => toast.error(e.message),
  });
  const unlinkMutation = (trpc as any).branches.unlinkService.useMutation({
    onSuccess: () => {
      toast.success("Vínculo removido!");
      (utils as any).branches.getServices.invalidate({ branchId: branch.id });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const isLoading = loadingLinks || loadingServices;

  function toggle(serviceId: number, isLinked: boolean) {
    if (isLinked) {
      unlinkMutation.mutate({ branchId: branch.id, serviceId });
    } else {
      linkMutation.mutate({ branchId: branch.id, serviceId });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Selecione os serviços oferecidos nesta filial. No agendamento público, quando um serviço estiver em mais de uma filial, o cliente poderá escolher a unidade.
      </p>
      {(allServices as any[]).length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          <Scissors className="w-8 h-8 mx-auto mb-2 opacity-30" />
          Nenhum serviço cadastrado ainda.
        </div>
      ) : (
        <div className="space-y-2">
          {(allServices as any[]).map((svc: any) => {
            const isLinked = (linkedIds as number[]).includes(svc.id);
            return (
              <div
                key={svc.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  isLinked ? "border-primary/40 bg-primary/5" : "border-border hover:bg-muted/40"
                }`}
                onClick={() => toggle(svc.id, isLinked)}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isLinked ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                }`}>
                  <Scissors className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{svc.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {svc.duration} min · R$ {Number(svc.price ?? 0).toFixed(2).replace(".", ",")}
                  </p>
                </div>
                <Badge variant={isLinked ? "default" : "outline"} className="text-xs shrink-0">
                  {isLinked ? "Vinculado" : "Não vinculado"}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Painel de detalhes de uma filial (métricas + horários) ──────────────────
// ── Painel de Agente de IA por Filial ────────────────────────────────────────────────
function BranchAgentPanel({ branch }: { branch: Branch }) {
  const utils = trpc.useUtils();
  const origin = window.location.origin;
  const webhookUrl = `${origin}/api/agent/webhook/branch/${branch.id}`;

  const { data: agent, isLoading: agentLoading } = (trpc as any).branches.getAgent.useQuery(
    { branchId: branch.id },
    { enabled: !!branch.id }
  );

  const { data: evoStatus, isLoading: evoLoading, refetch: refetchStatus } = (trpc as any).branches.getEvolutionStatus.useQuery(
    { branchId: branch.id },
    { enabled: !!branch.id, refetchInterval: 15000 }
  );

  const [agentForm, setAgentForm] = useState({
    name: "",
    personality: "",
    welcomeMessage: "",
    tone: "friendly" as "friendly" | "formal" | "casual",
    canBook: true,
    canCancel: true,
    canReschedule: true,
    canAnswerFaq: true,
    isActive: true,
    maxResponseLength: 500,
  });

  const [evoForm, setEvoForm] = useState({
    evolutionApiUrl: "",
    evolutionApiKey: "",
    evolutionInstance: "",
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [qrText, setQrText] = useState<string | null>(null);

  useEffect(() => {
    if (agent) {
      setAgentForm({
        name: agent.name ?? "",
        personality: agent.personality ?? "",
        welcomeMessage: agent.welcomeMessage ?? "",
        tone: (agent.tone as any) ?? "friendly",
        canBook: agent.canBook ?? true,
        canCancel: agent.canCancel ?? true,
        canReschedule: agent.canReschedule ?? true,
        canAnswerFaq: agent.canAnswerFaq ?? true,
        isActive: agent.isActive ?? true,
        maxResponseLength: agent.maxResponseLength ?? 500,
      });
    }
  }, [agent]);

  useEffect(() => {
    const b = branch as any;
    setEvoForm({
      evolutionApiUrl: b.evolutionApiUrl ?? "",
      evolutionApiKey: b.evolutionApiKey ?? "",
      evolutionInstance: b.evolutionInstance ?? "",
    });
  }, [branch]);

  const upsertAgentMutation = (trpc as any).branches.upsertAgent.useMutation({
    onSuccess: () => {
      toast.success("Agente salvo com sucesso!");
      (utils as any).branches.getAgent.invalidate({ branchId: branch.id });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const configureEvoMutation = (trpc as any).branches.configureEvolution.useMutation({
    onSuccess: () => {
      toast.success("Configuração da Evolution API salva!");
      (utils as any).branches.list.invalidate();
      refetchStatus();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const connectMutation = (trpc as any).branches.connectEvolution.useMutation({
    onSuccess: (data: any) => {
      if (data.connected) {
        toast.success("WhatsApp já conectado!");
        setQrCode(null);
      } else if (data.qrcode) {
        setQrCode(data.qrcode);
        setQrText(data.qrText ?? null);
        toast.info("Escaneie o QR Code com seu WhatsApp.");
      } else {
        toast.info("Instância criada. Aguardando conexão...");
      }
      refetchStatus();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const disconnectMutation = (trpc as any).branches.disconnectEvolution.useMutation({
    onSuccess: () => {
      toast.success("WhatsApp desconectado.");
      setQrCode(null);
      refetchStatus();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const setWebhookMutation = (trpc as any).branches.setEvolutionWebhook.useMutation({
    onSuccess: (data: any) => {
      toast.success(`Webhook configurado: ${data.webhookUrl}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const togglePauseMutation = (trpc as any).branches.toggleAgentPause.useMutation({
    onSuccess: (data: any) => {
      toast.success(data.paused ? "Agente pausado." : "Agente retomado.");
      (utils as any).branches.list.invalidate();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const isConnected = evoStatus?.connected === true;
  const evoState = evoStatus?.state ?? "unknown";
  const branchAny = branch as any;
  const isPaused = branchAny.agentPaused ?? false;

  if (agentLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status geral */}
      <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/20">
        <div className={`w-3 h-3 rounded-full ${isConnected ? "bg-green-500" : "bg-muted-foreground/40"}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            WhatsApp:{" "}
            {isConnected ? (
              <span className="text-green-500">Conectado</span>
            ) : evoState === "not_configured" ? (
              <span className="text-muted-foreground">Não configurado</span>
            ) : evoState === "connecting" ? (
              <span className="text-yellow-500">Conectando...</span>
            ) : (
              <span className="text-red-400">Desconectado</span>
            )}
          </p>
          {evoState !== "not_configured" && (
            <p className="text-xs text-muted-foreground font-mono">{branchAny.evolutionInstance || "—"}</p>
          )}
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => refetchStatus()} disabled={evoLoading}>
          <RefreshCw className={`w-3.5 h-3.5 ${evoLoading ? "animate-spin" : ""}`} />
        </Button>
        {agent && (
          <Button
            variant={isPaused ? "default" : "outline"}
            size="sm"
            className="gap-1.5 text-xs"
            onClick={() => togglePauseMutation.mutate({ branchId: branch.id, paused: !isPaused })}
            disabled={togglePauseMutation.isPending}
          >
            {isPaused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            {isPaused ? "Retomar" : "Pausar"}
          </Button>
        )}
      </div>

      {/* Evolution API */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Wifi className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Conexão WhatsApp (Evolution API)</h3>
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">URL da Evolution API</Label>
            <Input
              placeholder="https://evolution.meuservidor.com"
              value={evoForm.evolutionApiUrl}
              onChange={(e) => setEvoForm((f) => ({ ...f, evolutionApiUrl: e.target.value }))}
              className="text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">API Key</Label>
            <div className="relative">
              <Input
                type={showApiKey ? "text" : "password"}
                placeholder="Chave de acesso da Evolution API"
                value={evoForm.evolutionApiKey}
                onChange={(e) => setEvoForm((f) => ({ ...f, evolutionApiKey: e.target.value }))}
                className="text-sm pr-9"
              />
              <button
                type="button"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowApiKey((v) => !v)}
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Nome da Instância</Label>
            <Input
              placeholder="ex: filial-centro"
              value={evoForm.evolutionInstance}
              onChange={(e) => setEvoForm((f) => ({ ...f, evolutionInstance: e.target.value }))}
              className="text-sm font-mono"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 gap-1.5 text-xs"
              onClick={() => configureEvoMutation.mutate({ branchId: branch.id, ...evoForm })}
              disabled={configureEvoMutation.isPending || !evoForm.evolutionApiUrl || !evoForm.evolutionApiKey || !evoForm.evolutionInstance}
            >
              {configureEvoMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Salvar configuração
            </Button>
            {isConnected ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs text-destructive hover:text-destructive"
                onClick={() => disconnectMutation.mutate({ branchId: branch.id })}
                disabled={disconnectMutation.isPending}
              >
                {disconnectMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <WifiOff className="w-3.5 h-3.5" />}
                Desconectar
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                className="gap-1.5 text-xs"
                onClick={() => connectMutation.mutate({ branchId: branch.id })}
                disabled={connectMutation.isPending || !branchAny.evolutionApiUrl}
              >
                {connectMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <QrCode className="w-3.5 h-3.5" />}
                Conectar WhatsApp
              </Button>
            )}
          </div>
        </div>

        {qrCode && (
          <div className="flex flex-col items-center gap-3 p-4 rounded-lg border bg-white">
            <p className="text-sm font-medium text-gray-800">Escaneie com o WhatsApp</p>
            <img src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`} alt="QR Code WhatsApp" className="w-48 h-48 object-contain" />
            {qrText && (
              <p className="text-[10px] text-gray-500 font-mono break-all text-center max-w-xs">{qrText}</p>
            )}
            <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => connectMutation.mutate({ branchId: branch.id })}>
              <RefreshCw className="w-3.5 h-3.5" />
              Atualizar QR
            </Button>
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs">URL do Webhook</Label>
          <div className="flex items-center gap-2">
            <Input value={webhookUrl} readOnly className="text-xs font-mono bg-muted/30" />
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("URL copiada!"); }}
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-xs text-muted-foreground"
            onClick={() => setWebhookMutation.mutate({ branchId: branch.id, webhookBaseUrl: origin })}
            disabled={setWebhookMutation.isPending || !branchAny.evolutionApiUrl}
          >
            {setWebhookMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link className="w-3.5 h-3.5" />}
            Configurar webhook automaticamente
          </Button>
        </div>
      </div>

      <Separator />

      {/* Configuração do Agente */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Configuração do Agente de IA</h3>
          {agent && (
            <Badge variant={agent.isActive ? "default" : "secondary"} className="text-xs">
              {agent.isActive ? "Ativo" : "Inativo"}
            </Badge>
          )}
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Nome do agente</Label>
            <Input
              placeholder="Ex: Assistente Filial Centro"
              value={agentForm.name}
              onChange={(e) => setAgentForm((f) => ({ ...f, name: e.target.value }))}
              className="text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Mensagem de boas-vindas</Label>
            <Textarea
              placeholder="Olá! Sou a assistente da filial Centro. Como posso ajudar?"
              value={agentForm.welcomeMessage}
              onChange={(e) => setAgentForm((f) => ({ ...f, welcomeMessage: e.target.value }))}
              rows={2}
              className="text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Personalidade / instruções</Label>
            <Textarea
              placeholder="Descreva como o agente deve se comportar..."
              value={agentForm.personality}
              onChange={(e) => setAgentForm((f) => ({ ...f, personality: e.target.value }))}
              rows={3}
              className="text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Tom de voz</Label>
            <Select value={agentForm.tone} onValueChange={(v) => setAgentForm((f) => ({ ...f, tone: v as any }))}>
              <SelectTrigger className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="friendly">Amigável</SelectItem>
                <SelectItem value="formal">Formal</SelectItem>
                <SelectItem value="casual">Casual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {([
              { key: "canBook" as const, label: "Pode agendar" },
              { key: "canCancel" as const, label: "Pode cancelar" },
              { key: "canReschedule" as const, label: "Pode reagendar" },
              { key: "canAnswerFaq" as const, label: "Responde dúvidas" },
            ]).map(({ key, label }) => (
              <div key={key} className="flex items-center gap-2">
                <Switch
                  checked={agentForm[key]}
                  onCheckedChange={(v) => setAgentForm((f) => ({ ...f, [key]: v }))}
                />
                <Label className="text-xs cursor-pointer">{label}</Label>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={agentForm.isActive}
              onCheckedChange={(v) => setAgentForm((f) => ({ ...f, isActive: v }))}
            />
            <Label className="text-xs cursor-pointer">Agente ativo</Label>
          </div>
          <Button
            className="w-full gap-2"
            onClick={() => upsertAgentMutation.mutate({ branchId: branch.id, ...agentForm })}
            disabled={upsertAgentMutation.isPending || !agentForm.name}
          >
            {upsertAgentMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {agent ? "Salvar alterações" : "Criar agente"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function BranchDetailPanel({
  branch,
  onBack,
  onEdit,
}: {
  branch: Branch;
  onBack: () => void;
  onEdit: (b: Branch) => void;
}) {
  const [activeTab, setActiveTab] = useState("metrics");
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 text-muted-foreground">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Button>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-lg truncate">{branch.name}</h2>
          <p className="text-xs text-muted-foreground font-mono">/filial/{branch.slug}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => onEdit(branch)} className="gap-1.5">
          <Pencil className="w-3.5 h-3.5" />
          Editar
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Mobile: caixa suspensa */}
        <div className="block md:hidden mb-2">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Selecione a seção" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="metrics">Métricas</SelectItem>
              <SelectItem value="hours">Horários</SelectItem>
              <SelectItem value="professionals">Profissionais</SelectItem>
              <SelectItem value="services">Serviços</SelectItem>
              <SelectItem value="agent">Agente IA</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Desktop: abas normais */}
        <TabsList className="hidden md:grid md:grid-cols-5 w-full">
          <TabsTrigger value="metrics" className="gap-1 text-xs">
            <BarChart3 className="w-3.5 h-3.5" />
            Métricas
          </TabsTrigger>
          <TabsTrigger value="hours" className="gap-1 text-xs">
            <Clock className="w-3.5 h-3.5" />
            Horários
          </TabsTrigger>
          <TabsTrigger value="professionals" className="gap-1 text-xs">
            <Users className="w-3.5 h-3.5" />
            Profis.
          </TabsTrigger>
          <TabsTrigger value="services" className="gap-1 text-xs">
            <Scissors className="w-3.5 h-3.5" />
            Serviços
          </TabsTrigger>
          <TabsTrigger value="agent" className="gap-1 text-xs">
            <Bot className="w-3.5 h-3.5" />
            Agente IA
          </TabsTrigger>
        </TabsList>
        <TabsContent value="metrics" className="mt-4">
          <BranchMetricsPanel branch={branch} />
        </TabsContent>
        <TabsContent value="hours" className="mt-4">
          <BranchWorkingHoursPanel branch={branch} />
        </TabsContent>
        <TabsContent value="professionals" className="mt-4">
          <BranchProfessionalsPanel branch={branch} />
        </TabsContent>
        <TabsContent value="services" className="mt-4">
          <BranchServicesPanel branch={branch} />
        </TabsContent>
        <TabsContent value="agent" className="mt-4">
          <BranchAgentPanel branch={branch} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function AdminBranches() {
  const utils = trpc.useUtils();
  const { data: branches, isLoading, error } = (trpc as any).branches.list.useQuery();
  const { data: salon } = trpc.salon.getMySalon.useQuery();

  const [modalOpen, setModalOpen] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileRef = useRef<HTMLInputElement>(null);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Branch | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [form, setForm] = useState(emptyForm);

  const createMutation = (trpc as any).branches.create.useMutation({
    onSuccess: () => {
      toast.success("Filial criada com sucesso!");
      (utils as any).branches.list.invalidate();
      setModalOpen(false);
      setForm(emptyForm);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const updateMutation = (trpc as any).branches.update.useMutation({
    onSuccess: () => {
      toast.success("Filial atualizada!");
      (utils as any).branches.list.invalidate();
      setModalOpen(false);
      setEditingBranch(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const deleteMutation = (trpc as any).branches.delete.useMutation({
    onSuccess: () => {
      toast.success("Filial removida.");
      (utils as any).branches.list.invalidate();
      setDeleteTarget(null);
      if (selectedBranch?.id === deleteTarget?.id) setSelectedBranch(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const uploadLogoMutation = (trpc as any).branches.uploadLogo.useMutation({
    onSuccess: (data: any) => {
      setForm((f) => ({ ...f, logoUrl: data.logoUrl }));
      setLogoPreview(data.logoUrl);
      toast.success("Logo enviado com sucesso!");
      setLogoUploading(false);
    },
    onError: (e: any) => {
      toast.error(e.message);
      setLogoUploading(false);
    },
  });

  async function handleLogoFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem muito grande. Máximo 2 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setLogoPreview(base64);
      if (editingBranch) {
        setLogoUploading(true);
        uploadLogoMutation.mutate({ branchId: editingBranch.id, base64, mimeType: file.type });
      } else {
        setForm((f) => ({ ...f, logoUrl: base64 }));
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  function openCreate() {
    setEditingBranch(null);
    setForm(emptyForm);
    setLogoPreview("");
    setModalOpen(true);
  }

  function openEdit(branch: Branch) {
    setEditingBranch(branch);
    setForm({
      name: branch.name,
      slug: branch.slug,
      description: branch.description ?? "",
      address: branch.address ?? "",
      phone: branch.phone ?? "",
      email: branch.email ?? "",
      logoUrl: branch.logoUrl ?? "",
      status: branch.status,
    });
    setLogoPreview(branch.logoUrl ?? "");
    setModalOpen(true);
  }

  function handleSubmit() {
    if (editingBranch) {
      const { logoUrl, ...rest } = form;
      // logoUrl base64 = upload já foi feito pelo uploadLogoMutation; não reenviar
      updateMutation.mutate({ id: editingBranch.id, ...rest, ...(logoUrl && !logoUrl.startsWith("data:") ? { logoUrl } : {}) });
    } else {
      const { logoUrl, ...rest } = form;
      const isBase64 = logoUrl.startsWith("data:");
      createMutation.mutate({ ...rest, ...(isBase64 ? {} : { logoUrl }) }, {
        onSuccess: (branch: any) => {
          if (isBase64 && logoUrl) {
            setLogoUploading(true);
            uploadLogoMutation.mutate({ branchId: branch.id, base64: logoUrl, mimeType: "image/jpeg" });
          }
        },
      });
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const origin = window.location.origin;

  function copyLink(path: string) {
    navigator.clipboard.writeText(`${origin}${path}`);
    toast.success("Link copiado!");
  }

  // Feature gate
  if (error?.data?.code === "FORBIDDEN") {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Crown className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <CardTitle className="text-amber-300">Funcionalidade Elite</CardTitle>
                <CardDescription className="text-amber-400/70">Disponível apenas no plano Elite</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              O sistema de <strong>Filiais</strong> permite cadastrar múltiplas unidades do seu salão, cada uma com sua própria página de agendamento e loja de produtos.
            </p>
            <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold" onClick={() => window.location.href = "/admin/upgrade"}>
              <Crown className="w-4 h-4 mr-2" />
              Fazer upgrade para Elite
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      {!selectedBranch && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Building2 className="w-6 h-6 text-primary" />
                Filiais
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Gerencie as unidades do seu salão. Clique em uma filial para ver métricas e horários.
              </p>
            </div>
            <Button onClick={openCreate} className="gap-2">
              <Plus className="w-4 h-4" />
              Nova Filial
            </Button>
          </div>

          <Card className="border-blue-500/20 bg-blue-500/5">
            <CardContent className="flex items-start gap-3 pt-4 pb-4">
              <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-300/80">
                Cada filial ativa gera uma cobrança adicional na sua assinatura Elite. O valor é calculado proporcionalmente ao período restante do ciclo atual.
              </p>
            </CardContent>
          </Card>
          {branches && <BranchComparison branches={branches as Branch[]} />}
        </>
      )}

      {/* Painel de detalhe da filial selecionada */}
      {selectedBranch ? (
        <BranchDetailPanel
          branch={selectedBranch}
          onBack={() => setSelectedBranch(null)}
          onEdit={(b) => { openEdit(b); setSelectedBranch(null); }}
        />
      ) : (
        /* Lista de filiais */
        isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : !branches || branches.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
                <Building2 className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="font-semibold text-lg">Nenhuma filial cadastrada</p>
                <p className="text-sm text-muted-foreground mt-1">Crie sua primeira filial para expandir seu negócio.</p>
              </div>
              <Button onClick={openCreate} variant="outline" className="gap-2">
                <Plus className="w-4 h-4" />
                Criar primeira filial
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {branches.map((branch: Branch) => (
              <Card
                key={branch.id}
                className="relative overflow-hidden cursor-pointer hover:border-primary/40 transition-colors"
                onClick={() => setSelectedBranch(branch)}
              >
                <CardContent className="p-5 space-y-4">
                  {/* Status badge */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      {branch.logoUrl ? (
                        <img src={branch.logoUrl} alt={branch.name} className="w-10 h-10 rounded-lg object-cover border border-border" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-primary" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold leading-tight">{branch.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{branch.slug}</p>
                      </div>
                    </div>
                    <Badge variant={branch.status === "active" ? "default" : "secondary"} className="shrink-0">
                      {branch.status === "active" ? "Ativa" : "Inativa"}
                    </Badge>
                  </div>

                  {/* Infos */}
                  <div className="space-y-1.5 text-sm text-muted-foreground">
                    {branch.address && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{branch.address}</span>
                      </div>
                    )}
                    {branch.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 shrink-0" />
                        <span>{branch.phone}</span>
                      </div>
                    )}
                    {branch.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{branch.email}</span>
                      </div>
                    )}
                  </div>

                  {/* Links públicos */}
                  <div className="space-y-2 pt-1" onClick={(e) => e.stopPropagation()}>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Links públicos</p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-1.5 text-xs"
                        onClick={() => window.open(`${origin}/filial/${branch.slug}`, "_blank")}
                      >
                        <CalendarDays className="w-3.5 h-3.5" />
                        Agendamento
                        <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-1.5 text-xs"
                        onClick={() => window.open(`${origin}/filial/${branch.slug}/loja`, "_blank")}
                      >
                        <Store className="w-3.5 h-3.5" />
                        Loja
                        <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1 gap-1.5 text-xs text-muted-foreground"
                        onClick={() => copyLink(`/filial/${branch.slug}`)}
                      >
                        <Copy className="w-3.5 h-3.5" />
                        Copiar link agendamento
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1 gap-1.5 text-xs text-muted-foreground"
                        onClick={() => copyLink(`/filial/${branch.slug}/loja`)}
                      >
                        <Copy className="w-3.5 h-3.5" />
                        Copiar link loja
                      </Button>
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="flex gap-2 pt-1 border-t border-border" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-1.5 text-xs"
                      onClick={() => setSelectedBranch(branch)}
                    >
                      <BarChart3 className="w-3.5 h-3.5" />
                      Métricas & Horários
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => openEdit(branch)}>
                      <Pencil className="w-3.5 h-3.5" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(branch)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}

      {/* Modal criar/editar */}
      <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) { setModalOpen(false); setEditingBranch(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBranch ? "Editar Filial" : "Nova Filial"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5">
                <Label>Nome da filial *</Label>
                <Input
                  placeholder="Ex: Unidade Centro"
                  value={form.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setForm((f) => ({
                      ...f,
                      name,
                      slug: editingBranch ? f.slug : slugify(name),
                    }));
                  }}
                />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Slug (URL) *</Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">/filial/</span>
                  <Input
                    placeholder="unidade-centro"
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value) }))}
                    disabled={!!editingBranch}
                    className="font-mono text-sm"
                  />
                </div>
                {editingBranch && (
                  <p className="text-xs text-muted-foreground">O slug não pode ser alterado após a criação.</p>
                )}
              </div>
            </div>

            {/* Logo da filial */}
            <div className="space-y-1.5">
              <Label>Logo da filial</Label>
              <div className="flex items-center gap-3">
                <div
                  className="w-16 h-16 rounded-xl border-2 border-dashed border-border flex items-center justify-center bg-muted/30 cursor-pointer hover:border-primary/50 transition-colors overflow-hidden shrink-0"
                  onClick={() => logoFileRef.current?.click()}
                >
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <ImagePlus className="w-6 h-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col gap-1.5 flex-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full text-xs h-8"
                    onClick={() => logoFileRef.current?.click()}
                    disabled={logoUploading}
                  >
                    {logoUploading ? (
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <ImagePlus className="w-3 h-3 mr-1" />
                    )}
                    {logoPreview ? "Trocar logo" : "Enviar logo"}
                  </Button>
                  {logoPreview && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs h-7 text-destructive hover:text-destructive"
                      onClick={() => {
                        setLogoPreview("");
                        setForm((f) => ({ ...f, logoUrl: "" }));
                        if (editingBranch) {
                          updateMutation.mutate({ id: editingBranch.id, logoUrl: "" });
                        }
                      }}
                    >
                      <X className="w-3 h-3 mr-1" />
                      Remover logo
                    </Button>
                  )}
                  <p className="text-[10px] text-muted-foreground">JPG, PNG ou WebP · máx. 2 MB</p>
                </div>
              </div>
              <input
                ref={logoFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleLogoFile}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Descrição</Label>
              <Textarea
                placeholder="Breve descrição da filial..."
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Endereço</Label>
              <Input
                placeholder="Rua, número, bairro, cidade"
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Telefone</Label>
                <Input
                  placeholder="(11) 99999-9999"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>E-mail</Label>
                <Input
                  type="email"
                  placeholder="filial@salao.com"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
            </div>

            {editingBranch && (
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v as "active" | "inactive" }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativa</SelectItem>
                    <SelectItem value="inactive">Inativa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={isSaving || !form.name || !form.slug}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingBranch ? "Salvar alterações" : "Criar filial"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de exclusão */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover filial?</AlertDialogTitle>
            <AlertDialogDescription>
              A filial <strong>{deleteTarget?.name}</strong> será removida permanentemente. Os links públicos deixarão de funcionar. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate({ id: deleteTarget.id })}
            >
              {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Remover"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
